import { MOTION, RADIUS } from "@elyxeer/ui";
import type { SvgIconComponent } from "@mui/icons-material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";

/**
 * Where a scrolling row starts and ends on a phone. The page is edge-to-edge, so this
 * padding lives inside the scroll container: it aligns the resting first item with the
 * page content, then scrolls away with the items. The extra 2px keeps the first focus
 * ring intact; the trailing side is wider so the last item clears the edge fade.
 */
const ROW_START = { xs: 2.75, md: 0 } as const;
const ROW_END = { xs: 3.5, md: 0 } as const;
/** On desktop the row wraps instead, and lines up with the page gutter. */
const ROW_GUTTER = { xs: 0, md: 5 } as const;

/**
 * Fades the trailing edge of a row that runs off the screen, so a chip cut in half
 * reads as "there is more this way" rather than as a rendering fault. A mask, not an
 * overlay: the page behind is a gradient, and a painted fade would never match it.
 * Only the alpha of a mask is used, so the colour names are not colours here.
 */
const EDGE_FADE =
  "linear-gradient(to right, transparent 0, black 14px, black calc(100% - 28px), transparent 100%)";

type Appearance = "tabs" | "chips";

interface Option<T extends string> {
  value: T;
  count: number;
}

/**
 * One row of the library's filter, over one closed vocabulary.
 *
 * The library filters on two layers — format and topic — and they compose, so the
 * two rows share one control: the same list semantics, the same scroll behaviour and
 * the same explicit "all" item. They differ only in how they look, and deliberately:
 *
 * - **`tabs`** is for format, the coarse "what kind of thing" choice. An underlined
 *   strip with the same glyphs the tiles carry reads as switching *views* of the
 *   library, which is what a format is.
 * - **`chips`** is for topic, the finer "what about" choice that narrows the view.
 *
 * Two rows of identical outlined pills, each topped by an overline, made the member
 * parse a form before reaching the first article; two visibly different controls need
 * no headings, so the label is the list's accessible name only.
 *
 * Each row is single-select with an explicit "all" rather than a deselect gesture:
 * the unfiltered library is a legitimate view, so it deserves a control of its own
 * instead of being the state you reach by tapping the current filter again.
 *
 * Every member of the vocabulary is shown, including the ones at zero, because both
 * lists come back complete from the API. A row that reshuffles as the library fills
 * up is a worse control than one that occasionally reads "nothing here yet", and an
 * empty Imunita tells the member something true — its chip is drawn dashed.
 *
 * The rows scroll sideways on a phone and wrap on desktop. Two rows of eight and four
 * wrapped on a 390px screen would push the first tile off the bottom; one scrollable
 * row each keeps the content above the fold. The scrollbar is hidden because the row
 * is touch-scrolled, and a visible one would sit under the chips on macOS.
 *
 * Rendered as a labelled list: these are navigation between views, not a form
 * control, so a screen reader should hear how many options there are and which one
 * is active rather than a combobox that never opens.
 */
export function FacetFilter<T extends string>({
  label,
  allLabel,
  options,
  selected,
  onSelect,
  labelFor,
  appearance,
  iconFor,
  allIcon,
  testIdPrefix,
}: {
  /** The list's accessible name, e.g. "Formát". */
  label: string;
  allLabel: string;
  options: Option<T>[];
  /** The active value, or `null` for "all". */
  selected: T | null;
  onSelect: (value: T | null) => void;
  labelFor: (value: T) => string;
  appearance: Appearance;
  /** Leading glyph per value. Only the `tabs` appearance draws it. */
  iconFor?: (value: T) => SvgIconComponent;
  allIcon?: SvgIconComponent;
  /** `protocols.topic` yields `protocols.topic-all` and `protocols.topic-<value>`. */
  testIdPrefix: string;
}) {
  const items: {
    key: string;
    value: T | null;
    label: string;
    icon?: SvgIconComponent;
    empty: boolean;
  }[] = [
    { key: "all", value: null, label: allLabel, icon: allIcon, empty: false },
    ...options.map(({ value, count }) => ({
      key: value,
      value,
      label: labelFor(value),
      icon: iconFor?.(value),
      empty: count === 0,
    })),
  ];

  const Item = appearance === "tabs" ? FacetTab : FacetChip;

  return (
    <Box
      component="ul"
      aria-label={label}
      data-testid={`${testIdPrefix}-filter`}
      sx={{
        listStyle: "none",
        margin: 0,
        marginInline: ROW_GUTTER,
        paddingBlock: 0,
        paddingInlineStart: ROW_START,
        paddingInlineEnd: ROW_END,
        display: "flex",
        alignItems: "center",
        gap: appearance === "tabs" ? { xs: 2.5, md: 3 } : 1,
        flexWrap: { xs: "nowrap", md: "wrap" },
        overflowX: { xs: "auto", md: "visible" },
        // Without this the items are clipped rather than scrolled to on a phone.
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
        maskImage: { xs: EDGE_FADE, md: "none" },
        WebkitMaskImage: { xs: EDGE_FADE, md: "none" },
        // The tabs sit on a hairline that spans the row; the active one's underline
        // is drawn over it. Inset so the scroll container does not clip it.
        ...(appearance === "tabs"
          ? { boxShadow: "inset 0 -1px 0 var(--mui-palette-divider)" }
          : { paddingBlock: 0.25 }),
      }}
    >
      {items.map((item) => {
        const active = item.value === selected;

        return (
          <Box component="li" key={item.key} sx={{ flexShrink: 0 }}>
            <Item
              label={item.label}
              icon={item.icon}
              active={active}
              empty={item.empty}
              onClick={() => onSelect(item.value)}
              testId={`${testIdPrefix}-${item.key}`}
            />
          </Box>
        );
      })}
    </Box>
  );
}

interface ItemProps {
  label: string;
  icon?: SvgIconComponent;
  active: boolean;
  empty: boolean;
  onClick: () => void;
  testId: string;
}

function FacetTab({ label, icon: Icon, active, onClick, testId }: ItemProps) {
  return (
    <ButtonBase
      onClick={onClick}
      aria-current={active ? "true" : undefined}
      data-testid={testId}
      sx={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        minHeight: 44,
        paddingInline: 0.25,
        color: active ? "text.primary" : "text.muted",
        transition: `color ${MOTION.transition}`,
        "&:hover": { color: "text.primary" },
        // Grows from the centre rather than fading, so the eye follows the choice.
        "&::after": {
          content: '""',
          position: "absolute",
          insetInline: 0,
          bottom: 0,
          height: 2,
          borderRadius: `${RADIUS.full}px`,
          backgroundColor: "primary.main",
          transform: active ? "scaleX(1)" : "scaleX(0)",
          transition: `transform ${MOTION.transition}`,
        },
        // Inset: an outset ring would be clipped by the scroll container.
        "&.Mui-focusVisible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: -2,
          borderRadius: `${RADIUS.sm}px`,
        },
      }}
    >
      {Icon ? (
        <Icon
          sx={{
            fontSize: 20,
            color: active ? "link.main" : "inherit",
            transition: `color ${MOTION.transition}`,
          }}
        />
      ) : null}
      <Typography variant="L1" component="span" sx={{ whiteSpace: "nowrap" }}>
        {label}
      </Typography>
    </ButtonBase>
  );
}

function FacetChip({ label, active, empty, onClick, testId }: ItemProps) {
  return (
    <Chip
      label={label}
      onClick={onClick}
      icon={active ? <CheckRoundedIcon /> : undefined}
      color={active ? "primary" : "default"}
      variant={active ? "filled" : "outlined"}
      aria-current={active ? "true" : undefined}
      data-testid={testId}
      sx={{
        height: 36,
        borderRadius: `${RADIUS.full}px`,
        paddingInline: 0.5,
        fontSize: "0.875rem",
        transition: `background-color ${MOTION.transition}, color ${MOTION.transition}`,
        "& .MuiChip-icon": {
          fontSize: 18,
          color: "inherit",
          marginInlineStart: 0.75,
          marginInlineEnd: -0.5,
        },
        // Resting chips are lifted onto the card surface, so the row reads as the
        // same material as the tiles below it rather than as holes in the page.
        ...(active
          ? {}
          : {
              backgroundColor: "background.paper",
              borderColor: "divider",
              color: "text.secondary",
              // Nothing to read is still a real answer, so an empty topic stays
              // selectable — it is just visibly quieter than the rest of the row.
              ...(empty ? { borderStyle: "dashed", color: "text.muted" } : {}),
              "&.MuiChip-clickable:hover": {
                backgroundColor: "surface.inset",
                color: "text.primary",
              },
            }),
      }}
    />
  );
}
